import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { fundraisingStrategiesNonprofits } from '../content/educational/fundraisingStrategiesNonprofits';

const generatePDF = async (content, title, keywords) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 35;
    const footerHeight = 12;

    // Font sizes
    const fonts = {
      title: 22,
      sectionHeading: 14,
      subHeading: 12,
      body: 11,
      footer: 8
    };

    // Colors
    const colors = {
      blue: [37, 99, 235],
      green: [5, 150, 105],
      gray: [31, 41, 55],
      white: [255, 255, 255]
    };

    // Generate QR Codes
    const qrCodeAcademy = await QRCode.toDataURL('https://causeandimpact.org/academy');
    const qrCodeDonate = await QRCode.toDataURL('https://causeandimpact.org/donate');

    // Header component
    const addHeader = () => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      
      // Blue shadow effect
      doc.setTextColor(...colors.blue);
      doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 20.1, { align: 'center' });
      
      // Green overlay
      doc.setTextColor(...colors.green);
      doc.text('Cause & Impact, Inc.', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(...colors.white);
      doc.text('Expert Resources for Nonprofit Success', pageWidth / 2, 30, { align: 'center' });

      doc.addImage(qrCodeAcademy, 'PNG', pageWidth - margin - 25, 10, 20, 20);
    };

    // Footer component
    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fonts.footer);
      doc.setTextColor(...colors.white);
      doc.text(
        'Cause & Impact, Inc. | EIN: 84-3918881 | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 5,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
    };

    // Add Call to Action component
    const addCallToAction = (yPosition) => {
      const ctaHeight = 45;
      const qrSize = 28;
      
      // Add a subtle gradient effect for the main CTA box
      doc.setFillColor(...colors.blue);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 6, 6, 'F');
      
      // Main CTA text with better positioning
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(...colors.white);
      doc.text('Found this helpful?', pageWidth / 2, yPosition + 18, { align: 'center' });
      
      // Subtitle with call to action
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Scan to donate to one of our many Causes.', pageWidth / 2, yPosition + 28, { align: 'center' });
      
      // Enhanced QR code presentation with better alignment
      const qrX = pageWidth - qrSize - margin - 6;
      const qrY = yPosition + 8;
      
      // Add layered shadow effect for depth (adjusted offsets)
      doc.setFillColor(0, 0, 0, 0.06);
      doc.roundedRect(qrX + 0.8, qrY + 0.8, qrSize, qrSize, 2, 2, 'F');
      doc.setFillColor(0, 0, 0, 0.04);
      doc.roundedRect(qrX + 0.4, qrY + 0.4, qrSize, qrSize, 2, 2, 'F');
      
      // Add white background for QR code
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(qrX, qrY, qrSize, qrSize, 2, 2, 'F');
      
      // Add the QR code image (aligned with background)
      doc.addImage(qrCodeDonate, 'PNG', qrX, qrY, qrSize, qrSize);
    };

    // Add markdown processing
    const processMarkdown = (text) => {
      let segments = [];
      const boldRegex = /\*\*(.*?)\*\*/g;
      let lastIndex = 0;
      let match;
      
      // Special handling for checklist items
      if (text.startsWith("'") || text.startsWith('✓')) {
        return [{
          text: text.trim(),
          bold: false
        }];
      }
      
      while ((match = boldRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          segments.push({
            text: text.substring(lastIndex, match.index),
            bold: false
          });
        }
        segments.push({
          text: match[1],
          bold: true
        });
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < text.length) {
        segments.push({
          text: text.substring(lastIndex),
          bold: false
        });
      }
      
      return segments;
    };

    // Update the renderTextWithBold function to maintain consistent sizing
    const renderTextWithBold = (text, x, y, forcedSize = null) => {
      const segments = processMarkdown(text);
      let currentX = x;
      const currentSize = forcedSize || doc.getFontSize(); // Store current size
      
      segments.forEach(segment => {
        // Always maintain the specified size while only changing bold/normal
        doc.setFontSize(currentSize);
        doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
        doc.setTextColor(...colors.gray);
        doc.text(segment.text, currentX, y);
        currentX += doc.getTextWidth(segment.text);
      });
      
      // Restore size after rendering
      doc.setFontSize(currentSize);
    };

    // Process list with proper formatting
    const processList = (text, yPosition) => {
      const lines = text.split('\n');
      let currentY = yPosition;
      const bulletIndent = 12;
      const subIndent = 8;

      // Define consistent sizes that won't change
      const SIZES = {
        HEADER: 14,
        SUBHEADER: 12,
        BODY: 11,
        BULLET: 11
      };

      // Store font settings with explicit sizes
      const fontSettings = {
        header: { size: SIZES.HEADER, style: 'bold', color: colors.green },
        body: { size: SIZES.BODY, style: 'normal', color: colors.gray },
        bullet: { size: SIZES.BULLET, style: 'normal', color: colors.gray }
      };

      // Track current font state
      let currentFontState = {
        size: SIZES.BODY,
        style: 'normal',
        color: colors.gray
      };

      // Enhanced font state management
      const setFontState = (state, force = false) => {
        if (force || 
            state.size !== currentFontState.size || 
            state.style !== currentFontState.style || 
            state.color !== currentFontState.color) {
          currentFontState = { ...state };
          doc.setFont('helvetica', state.style);
          doc.setFontSize(state.size);
          doc.setTextColor(...state.color);
        }
      };

      // Add these functions before checkPageBreak
      const saveFontState = () => {
        return {
          size: currentFontState.size,
          style: currentFontState.style,
          color: currentFontState.color
        };
      };

      const restoreFontState = (state) => {
        setFontState(state, true);
      };

      // Enhanced page break handler
      const checkPageBreak = (height = 4) => {
        if (currentY + height > pageHeight - footerHeight - 15) {
          const savedState = saveFontState();
          
          doc.addPage();
          addHeader();
          addFooter(doc.internal.getNumberOfPages());
          currentY = headerHeight + 12;
          
          restoreFontState(savedState);
          return true;
        }
        return false;
      };

      // Enhanced table handling
      const handleTable = (tableText) => {
        const tableLines = tableText
          .replace('[TABLE]\n', '')
          .replace('[/TABLE]', '')
          .split('\n')
          .filter(line => line.trim());

        const headers = tableLines[0].split('|').map(cell => cell.trim());
        const rows = tableLines.slice(1).map(line => 
          line.split('|').map(cell => cell.trim())
        );

        doc.autoTable({
          startY: currentY,
          head: [headers],
          body: rows,
          theme: 'grid',
          styles: {
            fontSize: SIZES.BODY,
            cellPadding: 8,
            lineColor: [200, 200, 200],
            lineWidth: 0.1,
            font: 'helvetica'
          },
          headStyles: {
            fillColor: colors.blue,
            textColor: colors.white,
            fontStyle: 'bold'
          },
          columnStyles: {
            0: { fontStyle: 'bold' }
          },
          margin: { left: margin, right: margin },
          didDrawPage: (data) => {
            addHeader();
            addFooter(doc.internal.getNumberOfPages());
          }
        });

        currentY = doc.lastAutoTable.finalY + 10;
        return currentY;
      };

      // Process each line
      lines.forEach(line => {
        const trimmedLine = line.trim();

        // Handle tables
        if (trimmedLine === '[TABLE]') {
          let tableContent = '';
          let i = lines.indexOf(line);
          while (i < lines.length && !lines[i].includes('[/TABLE]')) {
            tableContent += lines[i] + '\n';
            i++;
          }
          tableContent += lines[i];
          currentY = handleTable(tableContent);
          return;
        }

        // Handle section headers
        if (trimmedLine.startsWith('###')) {
          checkPageBreak(8);
          setFontState(fontSettings.header);
          doc.text(trimmedLine.replace('###', '').trim(), margin, currentY);
          currentY += 8;
          setFontState(fontSettings.body);
          return;
        }

        // Handle bullet points with consistent sizing
        if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || trimmedLine.startsWith('*')) {
          const bulletText = trimmedLine.substring(1).trim();
          const indent = trimmedLine.startsWith('   ') ? bulletIndent + subIndent : bulletIndent;
          
          // Set consistent bullet point size
          const bulletSize = SIZES.BULLET;
          doc.setFontSize(bulletSize);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...colors.gray);
          
          // Draw bullet
          doc.text('•', margin + indent - 4, currentY);
          
          // Process text with forced size
          const textLines = doc.splitTextToSize(bulletText, contentWidth - (indent + 8));
          textLines.forEach((line, index) => {
            if (index > 0) {
              currentY += 5;
              if (checkPageBreak(5)) {
                // Explicitly reset size after page break
                doc.setFontSize(bulletSize);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(...colors.gray);
              }
            }
            renderTextWithBold(line, margin + indent + 4, currentY, bulletSize);
          });
          
          currentY += 6;
          return;
        }

        // Handle regular text
        if (trimmedLine) {
          checkPageBreak(6);
          setFontState(fontSettings.body);
          
          const textLines = doc.splitTextToSize(trimmedLine, contentWidth);
          textLines.forEach(line => {
            renderTextWithBold(line, margin, currentY);
            currentY += 5;
          });
        } else {
          currentY += 3;
        }
      });

      return currentY;
    };

    // Content generation with enhanced spacing
    const addContent = () => {
      let yPosition = headerHeight + 12;
      let pageNumber = 1;

      // Add title
      doc.setFontSize(fonts.title);
      doc.setTextColor(...colors.blue);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(title, contentWidth);
      doc.text(titleLines, pageWidth / 2, yPosition + 2, { align: 'center' });
      yPosition += (titleLines.length * 10) + 15;

      // Process each section
      content.forEach((section, index) => {
        // Check if section will fit
        if (yPosition + 50 > pageHeight - footerHeight - 15) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 12;
        }

        // Section Heading
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(fonts.sectionHeading);
        doc.setTextColor(...colors.green);
        doc.text(section.heading, margin, yPosition);
        yPosition += 12;

        // Section content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fonts.body);
        doc.setTextColor(...colors.gray);

        const paragraphs = section.text.split('\n\n');
        paragraphs.forEach(paragraph => {
          if (yPosition + 50 > pageHeight - footerHeight - 15) {
            doc.addPage();
            pageNumber++;
            addHeader();
            addFooter(pageNumber);
            yPosition = headerHeight + 12;
          }

          yPosition = processList(paragraph, yPosition);
          yPosition += 6;
        });

        yPosition += 15;
      });

      // Add CTA if space allows
      if (yPosition + 50 > pageHeight - footerHeight - 15) {
        doc.addPage();
        pageNumber++;
        addHeader();
        addFooter(pageNumber);
        yPosition = headerHeight + 12;
      }

      yPosition += 8;
      addCallToAction(yPosition);
    };

    // Generate PDF
    addHeader();
    addFooter(1);
    addContent();

    return doc;
  } catch (error) {
    console.error('Error generating PDF:', error);
    return null;
  }
};

export const generateFundraisingStrategiesOverview = async () => {
  return generatePDF(
    fundraisingStrategiesNonprofits.sections,
    fundraisingStrategiesNonprofits.title,
    fundraisingStrategiesNonprofits.keywords
  );
}; 